import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  MapPin,
  Trophy,
  Briefcase,
  Award,
  Globe2,
  Plane,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const PATH = "/destinations/asia-dmc";
const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Destinations", path: "/destinations" },
  { name: "Asia DMC", path: PATH },
];

const COUNTRY_HUBS = [
  { to: "/dubai-dmc", name: "Dubai", country: "UAE", note: "GCC HQ · MICE & corporate travel partner" },
  { to: "/singapore-dmc", name: "Singapore", country: "Singapore", note: "Singapore MICE DMC · Marina Bay Sands · Suntec" },
  { to: "/malaysia-dmc", name: "Malaysia", country: "Malaysia", note: "Kuala Lumpur B2B DMC · KLCC · MITEC" },
  { to: "/thailand-dmc", name: "Thailand", country: "Thailand", note: "Bangkok MICE organizer · Phuket corporate travel" },
  { to: "/hong-kong-dmc", name: "Hong Kong", country: "HK SAR", note: "Harbour events · HKCEC · AsiaWorld-Expo" },
  { to: "/vietnam-dmc", name: "Vietnam", country: "Vietnam", note: "Hanoi · HCMC · Ha Long Bay incentive specialist" },
  { to: "/philippines-dmc", name: "Philippines", country: "Philippines", note: "Manila · Cebu · Palawan island incentive DMC" },
];

const DIFFERENTIATORS = [
  "Single accountable Asia DMC partner — one contract, one P&L, one team across 7 countries",
  "Two decades of operating depth in MICE, incentive and corporate group travel",
  "140,000+ travellers managed for corporate planners, MICE organizers and outbound agents",
  "Local on-ground operations teams in every destination — venue, transport, F&B, AV, visa",
  "48-hour proposal turnaround on most B2B briefs",
  "24/7 ground support across all 7 Asia DMC destinations",
];

const FAQS = [
  {
    q: "What is an Asia DMC and why do corporate planners need one?",
    a: "An Asia DMC (Destination Management Company) is a B2B operating partner that handles the full ground logistics — venue, accommodation, transport, F&B, event production, visa — for corporate, MICE and incentive groups travelling into Asian destinations. Corporate travel planners, MICE organizers and outbound travel agents use a single Asia DMC like Trawelldmc to avoid coordinating dozens of local vendors across multiple countries.",
  },
  {
    q: "Which Asian destinations does Trawelldmc operate?",
    a: "Trawelldmc operates as a full-service DMC across seven destinations: Dubai (UAE — our HQ), Singapore, Malaysia (Kuala Lumpur, Penang, Langkawi), Thailand (Bangkok, Phuket, Chiang Mai), Hong Kong, Vietnam (Hanoi, Ho Chi Minh City, Ha Long Bay, Da Nang), and the Philippines (Manila, Cebu, Palawan, Boracay). Each destination is delivered by an in-region operations team under one accountable contract.",
  },
  {
    q: "Can a single Asia DMC handle multi-country corporate programmes?",
    a: "Yes — multi-country Asia programmes are one of our most-requested formats. We routinely deliver itineraries spanning Singapore → Bali, Dubai → Bangkok → Phuket, Hong Kong → Hanoi → Ha Long Bay, or full GCC-to-Southeast-Asia sweeps. One named programme manager owns the full journey; in-region teams execute on the ground; one consolidated invoice goes to your finance team.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function AsiaDmc() {
  return (
    <div data-testid="asia-dmc-hub">
      <Seo
        path={PATH}
        title="Asia DMC — Destination Management Company for B2B MICE & Corporate Travel | Trawelldmc"
        description="The Asia DMC trusted by corporate travel planners, MICE organizers and outbound agents. Single B2B partner across Dubai, Singapore, Malaysia, Thailand, Hong Kong, Vietnam & Philippines. 20+ years. 140,000+ travellers."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.singapore}
          alt="Asia DMC — destination management across Singapore, Dubai, Thailand and Southeast Asia"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">Asia DMC · B2B Destination Management</div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Asia</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              The single Asia DMC partner trusted by corporate travel planners, MICE organizers,
              and outbound travel agents — across seven destinations, two decades, 140,000+
              travellers.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inquiry?type=Asia" className="btn-primary" data-testid="asia-cta-rfp">
                Submit an Asia RFP <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="asia-cta-ai-quote">Instant AI estimate</Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY ASIA DMC */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why an Asia DMC</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              One B2B partner. <br />Seven Asia DMC markets.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Asia is the most operationally complex region in global corporate travel. Seven major
              MICE destinations across two regulatory blocs (GCC + ASEAN), four working languages,
              wildly different procurement environments, and visa regimes that change quarterly.
              Coordinating that complexity through a single Asia DMC — rather than five separate
              local agencies — is what separates predictable B2B programmes from chaos.
            </p>
            <p>
              Trawelldmc is built specifically for the B2B Asia DMC use case. Our clients are
              corporate travel planners, MICE organizers, conference producers, and outbound
              travel agents based in North America, Europe, India and the GCC who need a single
              accountable partner for their Asia work. We don&apos;t sell to leisure consumers. We
              don&apos;t dilute focus with retail. We&apos;re a B2B Asia DMC — and that focus shows up in
              proposal speed, contract terms, and on-ground delivery.
            </p>
            <p>
              With two decades of operating depth across Dubai, Singapore, Malaysia, Thailand,
              Hong Kong, Vietnam and the Philippines, we hold direct rate agreements with every
              major convention venue and 5-star hotel chain across the region. That depth
              translates into faster proposals, tighter budgets, and fewer surprises — the three
              things every MICE organizer measures their Asia DMC against.
            </p>
          </div>
        </div>
      </section>

      {/* COUNTRY SILOS GRID */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Country DMC silos</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Seven Asia DMC destinations. <br />Country-level expertise.
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed">
              Click any country below for the dedicated Asia DMC silo page — venues, services,
              FAQs and B2B contact paths specific to that destination.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {COUNTRY_HUBS.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="card-flat p-7 group hover:border-[#FF5238] transition-colors"
                data-testid={`asia-hub-${c.name.toLowerCase().replace(/ /g, "-")}`}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-[#FF5238]" />
                  <div className="overline">{c.country}</div>
                </div>
                <div className="font-heading text-2xl font-medium text-[#0B192C] mt-3 group-hover:text-[#FF5238] transition-colors">
                  {c.name} DMC
                </div>
                <div className="text-sm text-slate-500 mt-2 leading-relaxed">{c.note}</div>
                <div className="inline-flex items-center gap-1.5 text-sm text-[#FF5238] font-medium mt-5 group-hover:gap-2 transition-all">
                  Explore {c.name} DMC <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline !text-[#FF5238] mb-4">Why Trawelldmc</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              The B2B Asia DMC <br /><span className="text-[#FF5238]">your buyers trust.</span>
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-5">
            {DIFFERENTIATORS.map((d, i) => (
              <li key={d} className="flex gap-4 items-start border-t border-white/15 pt-5" data-testid={`asia-diff-${i}`}>
                <CheckCircle2 className="h-6 w-6 text-[#FF5238] flex-shrink-0 mt-0.5" />
                <span className="text-lg text-white/90 leading-snug">{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">B2B services</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            What we deliver across Asia.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/mice-services" className="card-flat p-8 group hover:border-[#FF5238] transition-colors">
            <Trophy className="h-8 w-8 text-[#FF5238]" />
            <h3 className="font-heading text-2xl font-medium tracking-tight text-[#0B192C] mt-5 group-hover:text-[#FF5238] transition-colors">MICE Services</h3>
            <p className="text-slate-600 mt-3 leading-relaxed">Full conference production across Asia&apos;s premier convention venues and integrated resorts.</p>
            <div className="inline-flex items-center gap-1.5 text-sm text-[#FF5238] font-medium mt-5 group-hover:gap-2 transition-all">
              Explore MICE services <ArrowRight className="h-4 w-4" />
            </div>
          </Link>
          <Link to="/corporate-travel" className="card-flat p-8 group hover:border-[#FF5238] transition-colors">
            <Briefcase className="h-8 w-8 text-[#FF5238]" />
            <h3 className="font-heading text-2xl font-medium tracking-tight text-[#0B192C] mt-5 group-hover:text-[#FF5238] transition-colors">Corporate Travel</h3>
            <p className="text-slate-600 mt-3 leading-relaxed">Dedicated account managers, 48-hour proposals, 24/7 ground ops for groups of 10–1,000+.</p>
            <div className="inline-flex items-center gap-1.5 text-sm text-[#FF5238] font-medium mt-5 group-hover:gap-2 transition-all">
              Explore corporate travel <ArrowRight className="h-4 w-4" />
            </div>
          </Link>
          <Link to="/incentive-travel" className="card-flat p-8 group hover:border-[#FF5238] transition-colors">
            <Award className="h-8 w-8 text-[#FF5238]" />
            <h3 className="font-heading text-2xl font-medium tracking-tight text-[#0B192C] mt-5 group-hover:text-[#FF5238] transition-colors">Incentive Travel</h3>
            <p className="text-slate-600 mt-3 leading-relaxed">Bespoke incentive programmes engineered across seven world-class Asia DMC destinations.</p>
            <div className="inline-flex items-center gap-1.5 text-sm text-[#FF5238] font-medium mt-5 group-hover:gap-2 transition-all">
              Explore incentive travel <ArrowRight className="h-4 w-4" />
            </div>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">FAQ</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Asia DMC — Frequently Asked Questions
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {FAQS.map((f, i) => (
              <div key={f.q} data-testid={`asia-faq-${i}`}>
                <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">{f.q}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Submit your Asia brief</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Brief your next <br /><span className="text-[#FF5238]">Asia DMC programme.</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Tell us your destination(s), headcount, dates and budget. A senior B2B account
              manager will respond within 48 hours with a costed proposal.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/inquiry?type=Asia" className="btn-primary w-full justify-center" data-testid="asia-bottom-cta">
              Submit an Asia RFP <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">Contact our B2B team</Link>
          </div>
        </div>
      </section>

      {/* ALSO EXPLORE */}
      <section className="bg-[#FDFCF8] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">Explore further</div>
          <nav className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/destinations" className="group flex items-start gap-3">
              <Globe2 className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">All destinations</div>
                <div className="text-sm text-slate-500 mt-1">Packages & itineraries</div>
              </div>
            </Link>
            <Link to="/mice-services" className="group flex items-start gap-3">
              <Trophy className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">MICE Services</div>
                <div className="text-sm text-slate-500 mt-1">Meetings, incentives, conferences, exhibitions</div>
              </div>
            </Link>
            <Link to="/blog" className="group flex items-start gap-3">
              <Briefcase className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Insights & Case Studies</div>
                <div className="text-sm text-slate-500 mt-1">B2B DMC resources</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">B2B DMC partner since 2004</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
